import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { bookHe, isMishnahSlug, isTalmudSlug, talmudDafLabel, talmudDafLabelEn } from "../lib/hebrewRef";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";
import { api, bookUrl, urlToSlug, stripNikud } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useLearning } from "../context/LearningContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import {
  ChevronRight,
  ChevronLeft,
  Settings2,
  Maximize2,
  Minimize2,
  BookOpen,
  Bookmark,
  BookMarked,
  ScrollText,
} from "lucide-react";
import { Button } from "../components/ui/button";
import VerseOverlay from "../components/VerseOverlay";
import ReaderSettings from "../components/ReaderSettings";
import ChapterPicker from "../components/ChapterPicker";
import BookCover from "../components/BookCover";
import BookReader3D from "../components/BookReader3D";
import BookReader3DEn from "../components/BookReader3DEn";
import BookNavRibbon from "../components/BookNavRibbon";
import PinchZoomStage from "../components/PinchZoomStage";
import DesktopReaderShell from "../components/DesktopReaderShell";
import MikraotShell from "../components/MikraotShell";
import ChapterActionsMenu from "../components/ChapterActionsMenu";
import ModeToggle from "../components/ModeToggle";
import { BasketSheet } from "../components/BasketSheet";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "react-i18next";

export default function Reader() {
  const { bookSlug, chapter: chapterStr } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEn } = useLanguage();
  const { t } = useTranslation();
  const slug = urlToSlug(bookSlug);
  const chapter = parseInt(chapterStr || "1", 10);
  // Derive the cover palette SYNCHRONOUSLY from the URL slug — no `book`
  // fetch needed. This prevents the navy-blue flash that used to appear
  // for ~200ms between mount and the moment /api/books/{slug} returned
  // (during which `book?.section` was undefined and the cover defaulted
  // to navy before flipping to teal). Tanakh slugs and unknowns stay on
  // the navy default; Talmud slugs go to the oxblood/dark-gold theme.
  const isTalmudBook = isTalmudSlug(bookSlug) || isTalmudSlug(slug);
  const coverTheme = isTalmudBook
    ? "talmud"
    : (isMishnahSlug(bookSlug) || isMishnahSlug(slug) ? "teal" : "navy");
  // Pre-computed Hebrew chapter label. For Tanakh/Mishnah this is a
  // single gematria letter ("א׳", "י״ב"). For Talmud Bavli it's the
  // full daf+amud label ("דף ב׳ ע״א") because the URL's integer
  // chapter is just a sequential index into the amudim list.
  // EN mode: arabic numerals for Tanakh/Mishnah ("1" / "12"), Talmud
  // keeps its own EN-aware label ("2a" / "2b").
  const chapterLabel = isTalmudBook
    ? (isEn && typeof talmudDafLabelEn === "function" ? talmudDafLabelEn(chapter) : talmudDafLabel(chapter))
    : (isEn ? String(chapter) : numberToHebrewLetter(chapter));
  // Unit word that prefixes the chapter label in headers. Tanakh and
  // Mishnah use "פרק", Talmud already includes "דף" inside its own
  // label so we leave the prefix empty. In English mode we switch to
  // "Chapter " — Talmud keeps the empty prefix (its label is "2a").
  const chapterUnitWord = isTalmudBook ? "" : (isEn ? "Chapter " : "פרק ");
  // chapterLabel for non-Talmud books: arabic numerals in EN, gematria
  // letters in HE.
  // Helper: format any sibling chapter index for the "prev/next" buttons
  // and progress badges. For Talmud the user thinks in dapim, so the
  // sibling button reads "דף ב׳ ע״ב" rather than "פרק 2".
  const chapterRefLabel = (n) =>
    isTalmudBook
      ? talmudDafLabel(n)
      : (isEn ? `Chapter ${n}` : `פרק ${numberToHebrewLetter(n)}`);
  // Verse from URL ?v=N — used by Search/Bookmarks/Notes links to deep-link
  // the user directly to a specific pasuk inside the chapter. We auto-open
  // the verse overlay AND flash a gold ring on the verse so the user can
  // visually find it without scanning.
  const targetVerse = parseInt(searchParams.get("v") || "0", 10) || null;

  const {
    fontSize,
    nikud,
    showVerseNums,
    focusMode,
    readingMode,
    autoContinue,
    update,
  } = useTheme();
  const { user } = useAuth();
  const learning = useLearning();

  const [data, setData] = useState(null);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVerse, setActiveVerse] = useState(null);
  // Note: deep-link navigation (`?v=N`) used to call `setActiveVerse`
  // which opened the VerseOverlay automatically. That conflated two
  // distinct actions — "go to this verse" vs. "open commentaries on
  // this verse". Per user request, deep-link nav now ONLY scroll-into-
  // views and gold-rings the verse via the CSS class
  // `.verse-deeplink-flash` (applied directly to the DOM element);
  // there is no React state for the flash because no other component
  // needs to react to it.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false); // closed-book intro shown when false
  const [opening, setOpening] = useState(false);
  // Opens BasketSheet (which can launch LessonBuilder) — used by
  // ChapterActionsMenu's "בנה שיעור על הפרק" entry.
  const [openChapterBasket, setOpenChapterBasket] = useState(false);
  const contentRef = useRef(null);
  const bookRef = useRef(null);           // exposes flipForward/flipBackward
  const searchInputRef = useRef(null);    // desktop left-panel search input
  const { isDesktop } = useBreakpoint();
  // Track current spread index (library's index, where 0 = last semantic page,
  // length-1 = first semantic page after the reverse-render trick). Updated
  // by BookReader3D's onPageFlip callback. Used to know when we're at the
  // book's start/end so the mobile sticky bar can fall back to chapter
  // navigation instead of doing nothing.
  const [libPageIdx, setLibPageIdx] = useState(null);
  const [libPageTotal, setLibPageTotal] = useState(0);

  // Highlights and notes for THIS chapter, derived from the global learning
  // store. No local cache — when the user marks a verse on any screen the
  // change appears here within one tick.
  const highlights = useMemo(
    () => learning.highlightsByVerse(slug, chapter),
    [learning, slug, chapter]
  );
  const notesByVerse = useMemo(
    () => learning.notesByVerse(slug, chapter),
    [learning, slug, chapter]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chapterData, bookData] = await Promise.all([
        api.chapter(slug, chapter),
        api.book(slug),
      ]);
      setData(chapterData);
      setBook(bookData);
    } catch (e) {
      toast.error(t("reader_extras.toast_chapter_load_error"));
    } finally {
      setLoading(false);
    }
  }, [slug, chapter]);

  // Compatibility shim — VerseOverlay still calls `onUserDataChange()` after
  // mutating. Now that mutations flow through the learning context, the cache
  // is already updated; we just trigger a context reload as a safety net to
  // re-sync if the server upserts (e.g. de-duplicated highlights).
  const loadUserData = useCallback(() => learning.reload(), [learning]);

  useEffect(() => {
    load();
    // Reset page-flip tracking on every chapter change so the mobile sticky
    // arrows recompute boundaries against the new chapter's page count.
    setLibPageIdx(null);
    setLibPageTotal(0);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [load]);

  // Idle-prefetch of the next chapter so a "next chapter" click feels
  // instant. Fires 800 ms after current chapter loads (don't fight for
  // bandwidth during the main render) and is fire-and-forget — the
  // browser caches the response and api.chapter() will reuse it. Cancels
  // cleanly on chapter change so we don't pile up requests.
  useEffect(() => {
    if (!data || !book) return undefined;
    const nextCh = chapter + 1;
    if (nextCh > (book.chapters || 0)) return undefined;
    const t = setTimeout(() => {
      api.chapter(slug, nextCh).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [data, book, slug, chapter]);

  // If the user returns to a reader they've been on before within this session, skip the closed-book intro.
  useEffect(() => {
    const seen = sessionStorage.getItem("tanakh_book_opened");
    if (seen === "1" || readingMode !== "book") {
      setBookOpen(true);
    } else {
      setBookOpen(false);
    }
  }, [readingMode, slug]);

  // Save reading progress
  useEffect(() => {
    if (user && data) {
      api.setProgress({ book: slug, chapter, verse: 1 }).catch(() => {});
    }
  }, [user, data, slug, chapter]);

  // Deep-link `?v=N` — auto-skip the closed-book intro, scroll/flip the
  // reader onto the verse, and flash a temporary gold ring around it so
  // the user immediately sees where the result is. Then strip the param
  // so a later re-visit re-fires the effect cleanly.
  //
  // INTENTIONALLY does NOT open the VerseOverlay (commentaries panel).
  // The user requested a strict separation: a deep-link from search /
  // bookmarks / notebook / verse-of-day must ONLY highlight + flash,
  // never simulate a click. A real tap on the verse still opens the
  // overlay through `handleVerseClick` further down the file.
  useEffect(() => {
    if (!data || !targetVerse) return;
    const verse = data.verses?.find((v) => v.verse === targetVerse);
    if (!verse) {
      // Verse param refers to a verse that doesn't exist in this
      // chapter — clean the URL so we don't keep re-entering this
      // effect on every render and the user can navigate freely.
      setSearchParams({}, { replace: true });
      return;
    }
    // Force open the book — the closed-book intro would otherwise hide it.
    sessionStorage.setItem("tanakh_book_opened", "1");
    setBookOpen(true);
    // (No setActiveVerse / setFlashVerse here — the only side-effect of
    // a deep-link is the CSS class applied to the DOM element below.)

    let cancelled = false;
    let polls = 0;
    let lastGoToCall = 0;
    let landed = false;       // once true → goToVerse is never called again
    const MAX_POLLS = 30;       // 30 × 200ms ≈ 6 s
    const POLL_MS = 200;
    // Book mode: 720ms flip animation. Re-issuing goToVerse during an
    // in-flight animation makes the library land on a DIFFERENT page
    // than expected (the "verse appears for 1s then jumps away" bug).
    // Wait 1.6s — comfortably > 2×flippingTime — between retries.
    const GOTO_RETRY_MS = 1600;
    const FLASH_DURATION_MS = 15000;  // ~15s gold ring per user spec

    const findVisible = () => {
      // Look for every variant of verse data-testid the readers emit:
      //   • Hebrew book mode  → BookPage    → `book-verse-{N}`
      //   • English book mode → BookPageEn  → `book-verse-en-{N}`
      //   • Study/mikraot HE  → MikraotShell outer span → `mikraot-verse-{N}`
      //   • Study/mikraot EN  → MikraotShell inner span → `mikraot-verse-en-{N}`
      // Missing any of these caused deep-links (Daily Verse, Notebook,
      // Highlights, Search → "open in book") to land but never reveal
      // the verse, especially in English book mode.
      const els = document.querySelectorAll(
        `[data-testid="book-verse-${targetVerse}"],` +
        `[data-testid="book-verse-en-${targetVerse}"],` +
        `[data-testid="mikraot-verse-${targetVerse}"],` +
        `[data-testid="mikraot-verse-en-${targetVerse}"]`
      );
      return Array.from(els).filter((el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      });
    };
    const flash = (els) => {
      els.forEach((el) => {
        el.classList.add("verse-deeplink-flash");
        // In SCROLL/STUDY mode we want to scroll the verse into view —
        // that path is rock-solid. In BOOK mode the verse is already
        // on the open spread (we just flipped to it); calling
        // scrollIntoView there would force the whole .tanakh-book-wrapper
        // to scroll, sometimes pushing the spread out of frame. Skip it
        // in book mode — the flip already centered the spread.
        if (readingMode !== "book") {
          el.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }
      });
      setSearchParams({}, { replace: true });
      setTimeout(() => {
        els.forEach((el) => el.classList.remove("verse-deeplink-flash"));
      }, FLASH_DURATION_MS);
    };
    let timer = 0;
    const tick = () => {
      if (cancelled) return;
      const visible = findVisible();
      if (visible.length > 0) {
        // We arrived. Stop ALL further goToVerse calls so a transient
        // layout flicker (e.g. ResizeObserver firing during animation
        // settle) cannot retrigger the flip — that was the root cause
        // of "verse appears for 1s then jumps to another page".
        landed = true;
        flash(visible);
        return;
      }
      const now = Date.now();
      if (
        !landed &&
        readingMode === "book" &&
        now - lastGoToCall > GOTO_RETRY_MS
      ) {
        bookRef.current?.goToVerse?.(targetVerse);
        lastGoToCall = now;
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        // 6s elapsed and we still can't see the verse — give up
        // gracefully. The URL is cleaned so subsequent navigations
        // don't get stuck retrying the same dead poll.
        setSearchParams({}, { replace: true });
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };
    // Initial flip attempt (best effort) — if the book is ready, the
    // first tick at +POLL_MS will catch the visible verse and short-
    // circuit. Otherwise the retry-with-cooldown loop takes over.
    if (readingMode === "book" && bookRef.current?.goToVerse) {
      bookRef.current.goToVerse(targetVerse);
      lastGoToCall = Date.now();
    }
    timer = setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, targetVerse]);

  // Mode-sync: whenever the active verse changes OR the user switches
  // reading mode, make sure the 3D book has flipped to the spread that
  // contains the active verse. Without this, switching from study to
  // book mode leaves the highlighted verse on an off-screen spread and
  // the user sees no highlight in the book even though state says
  // there's one. We poll so that book mount / animation timing never
  // leaves us calling goToVerse before pageFlip() is ready.
  useEffect(() => {
    if (readingMode !== "book") return;
    if (!activeVerse) return;
    let cancelled = false;
    let attempts = 0;
    const MAX = 30;          // ~4.5 s ceiling — enough for slowest mounts
    const INTERVAL = 150;
    const isVerseVisible = () => {
      // Match both HE and EN book-mode testids — see findVisible() above.
      const el = document.querySelector(
        `[data-testid="book-verse-${activeVerse.verse}"],` +
        `[data-testid="book-verse-en-${activeVerse.verse}"]`
      );
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };
    let lastFlip = 0;
    const tick = () => {
      if (cancelled) return;
      if (isVerseVisible()) return; // success — book is on the right spread
      const now = Date.now();
      if (now - lastFlip > 600) {
        bookRef.current?.goToVerse?.(activeVerse.verse);
        lastFlip = now;
      }
      attempts += 1;
      if (attempts >= MAX) return;
      setTimeout(tick, INTERVAL);
    };
    // First tick after a short settle so React has committed the new
    // BookReader3D tree on mode switch.
    const start = setTimeout(tick, 200);
    return () => { cancelled = true; clearTimeout(start); };
  }, [readingMode, activeVerse, data]);

  const total = book?.chapters || 0;
  const prevDisabled = chapter <= 1;
  const nextDisabled = total > 0 && chapter >= total;
  // "פרק י״ב / מ" for Tanakh/Mishnah, "ב / קכ״ה" — Talmud uses raw
  // integers in the count badge (dap'im are easier to scan numerically
  // when there are 125 of them than as gematria).
  const totalLabel = isTalmudBook
    ? `${chapter} / ${total}`
    : `${numberToHebrewLetter(chapter)} / ${numberToHebrewLetter(total)}`;

  const go = (delta) => {
    const target = chapter + delta;
    if (target < 1 || target > total) return;
    navigate(`/reader/${bookUrl(slug)}/${target}`);
  };

  // Keyboard shortcuts — desktop power-user controls.
  //   Arrow Right / Space : forward  (flip forward, or next chapter at end)
  //   Arrow Left          : backward (flip backward, or prev chapter at start)
  //   Esc                 : close verse panel / settings / picker
  //   Ctrl/Cmd + F        : focus left-panel search
  //   +  /  -             : increase / decrease font size
  useEffect(() => {
    const onKey = (e) => {
      // If typing in a text field — let the browser handle natural keys.
      const tag = (e.target?.tagName || "").toLowerCase();
      const inField =
        tag === "input" || tag === "textarea" || e.target?.isContentEditable;

      // Ctrl/Cmd + F → focus left search (only when panel exists)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
          searchInputRef.current.select?.();
        }
        return;
      }

      if (inField) return;

      // Esc — close whatever is open
      if (e.key === "Escape") {
        if (activeVerse) {
          setActiveVerse(null);
          e.preventDefault();
          return;
        }
        if (settingsOpen) {
          setSettingsOpen(false);
          e.preventDefault();
          return;
        }
        if (pickerOpen) {
          setPickerOpen(false);
          e.preventDefault();
          return;
        }
      }

      // Forward / backward
      if (e.key === "ArrowRight" || e.key === " ") {
        if (readingMode === "book" && bookOpen && bookRef.current?.flipForward) {
          bookRef.current.flipForward();
          e.preventDefault();
        } else if (!nextDisabled) {
          go(1);
          e.preventDefault();
        }
        return;
      }
      if (e.key === "ArrowLeft") {
        if (readingMode === "book" && bookOpen && bookRef.current?.flipBackward) {
          bookRef.current.flipBackward();
          e.preventDefault();
        } else if (!prevDisabled) {
          go(-1);
          e.preventDefault();
        }
        return;
      }

      // +/- font size (ThemeContext stores a string token 's'|'m'|'l'|'xl'|'2xl')
      if (e.key === "+" || e.key === "=") {
        const ladder = ["s", "m", "l", "xl", "2xl"];
        const idx = ladder.indexOf(fontSize);
        const next = ladder[Math.min(ladder.length - 1, (idx < 0 ? 1 : idx) + 1)];
        update({ fontSize: next });
        e.preventDefault();
        return;
      }
      if (e.key === "-" || e.key === "_") {
        const ladder = ["s", "m", "l", "xl", "2xl"];
        const idx = ladder.indexOf(fontSize);
        const next = ladder[Math.max(0, (idx < 0 ? 1 : idx) - 1)];
        update({ fontSize: next });
        e.preventDefault();
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    activeVerse,
    settingsOpen,
    pickerOpen,
    readingMode,
    bookOpen,
    nextDisabled,
    prevDisabled,
    fontSize,
    update,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    chapter, slug, total,
  ]);

  const handleOpenBook = () => {
    setOpening(true);
    setTimeout(() => {
      sessionStorage.setItem("tanakh_book_opened", "1");
      setBookOpen(true);
      setOpening(false);
    }, 850);
  };

  const handleVerseClick = (verse) => setActiveVerse(verse);

  const applyNikud = (text) => (nikud ? text : stripNikud(text));
  const sizeClass = `fs-${fontSize}`;
  const noNikudClass = nikud ? "" : "no-nikud";

  const toggleMode = () => {
    update({ readingMode: readingMode === "book" ? "study" : "book" });
    // We used to close the book when switching study → book in order to
    // replay the opening animation. That was reported as disorienting,
    // especially when an active verse is selected (the user would lose
    // their visual anchor). The opening intro now plays only on the
    // first visit per session — `sessionStorage["tanakh_book_opened"]`
    // controls that — and mode toggles preserve open state.
  };

  // ----------- CLOSED BOOK INTRO -----------
  if (readingMode === "book" && !bookOpen) {
    return (
      <div
        data-testid="book-entry-view"
        className="min-h-[calc(100vh-4rem)] flex flex-col items-center px-4 sm:px-6 py-6 sm:py-10 sm:justify-center pb-[calc(96px+env(safe-area-inset-bottom))] sm:pb-10"
      >
        <p className="font-latin italic text-[10px] sm:text-xs tracking-[0.35em] uppercase text-gold mb-4 sm:mb-10">
          <span className="hidden sm:inline">Tap the book to open</span>
          <span className="inline sm:hidden">{t("reader_extras.tap_to_open_phone")}</span>
        </p>
        {/* Mobile (incl. phone-landscape): half the viewport height ; Desktop: original xl size.
            The visibility uses a custom CSS class so that phone-LANDSCAPE
            (where width crosses 640px but height is tiny) also gets the
            small mobile cover instead of the oversized desktop one. */}
        <div className="phone-cover-only w-full flex items-center justify-center" data-testid="book-entry-cover-mobile">
          <BookCover
            opening={opening}
            onOpen={handleOpenBook}
            size="md"
            title={isEn ? (book?.en || slug || "Torah") : (book?.he || bookHe(slug))}
            subtitle={(book?.en || slug || "TORAH").toUpperCase()}
            theme={coverTheme}
          />
        </div>
        <div className="desktop-cover-only">
          <BookCover
            opening={opening}
            onOpen={handleOpenBook}
            size="xl"
            title={isEn ? (book?.en || slug || "Torah") : (book?.he || bookHe(slug))}
            subtitle={(book?.en || slug || "TORAH").toUpperCase()}
            theme={coverTheme}
          />
        </div>
        <p className="mt-6 sm:mt-16 font-heading text-xl sm:text-2xl text-foreground text-center">
          {isEn ? (book?.en || slug) : book?.he} <span className="text-gold mx-2">·</span> {chapterUnitWord}{chapterLabel}
        </p>
        <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-muted-foreground max-w-md text-center px-2">
          {isTalmudBook
            ? t("reader_chrome.study_mode_hint_page")
            : t("reader_chrome.study_mode_hint_chapter")}
        </p>

        {/* Desktop: inline action buttons. Mobile: hidden here, rendered as
            sticky bottom bar below so the user can always see them. */}
        <div className="mt-8 hidden sm:flex gap-3">
          <Button
            onClick={handleOpenBook}
            data-testid="open-book-cta"
            className="sacred-btn-gold"
          >
            <BookOpen className="h-4 w-4" strokeWidth={1.5} />
            {t("reader.open_book")}
          </Button>
          <Button
            variant="ghost"
            onClick={toggleMode}
            data-testid="switch-to-study-from-entry"
            data-mode-toggle="entry"
            id="mode-toggle-btn-entry"
            className="sacred-btn-ghost"
          >
            <ScrollText className="h-4 w-4" strokeWidth={1.5} />
            {t("reader_chrome.study_mode_label")}
          </Button>
        </div>

        {/* Mobile-only sticky action bar — always reachable without scrolling. */}
        <div
          data-testid="book-entry-sticky-actions"
          className="fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-gold/30 bg-background/95 backdrop-blur-md"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center gap-2 px-4 py-3">
            <Button
              onClick={handleOpenBook}
              data-testid="open-book-cta-mobile"
              className="sacred-btn-gold flex-1 justify-center"
            >
              <BookOpen className="h-4 w-4" strokeWidth={1.5} />
              {t("reader.open_book")}
            </Button>
            <Button
              variant="ghost"
              onClick={toggleMode}
              data-testid="switch-to-study-from-entry-mobile"
              className="sacred-btn-ghost flex-1 justify-center"
            >
              <ScrollText className="h-4 w-4" strokeWidth={1.5} />
              {t("reader_chrome.study_mode_label")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ----------- FLOATING EXIT FOCUS MODE BUTTON -----------
  // Always visible in focus mode so user can exit. The top/left offsets
  // honour `env(safe-area-inset-*)` so the button drops *below* the
  // iPhone Dynamic Island / status bar instead of being intercepted by
  // it (on desktop the inset resolves to 0px, so the button stays in
  // roughly the same place it always was). `min-h-[44px]` guarantees a
  // finger-sized hit area per Apple HIG.
  const ExitFocusButton = focusMode ? (
    <button
      type="button"
      data-testid="exit-focus-mode-btn"
      onClick={() => update({ focusMode: false })}
      aria-label={t("reader_extras.exit_focus_aria")}
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
        left: "calc(env(safe-area-inset-left, 0px) + 1rem)",
      }}
      className="fixed z-50 inline-flex items-center gap-2 rounded-full border-2 border-gold/60 bg-background/95 px-4 py-2 min-h-[44px] text-sm font-heading text-foreground shadow-lg backdrop-blur-md transition-all hover:border-gold hover:bg-gold/10 hover:scale-105 active:scale-95"
    >
      <Minimize2 className="h-4 w-4 text-gold" strokeWidth={2} />
      <span>{t("reader_extras.exit_focus_label")}</span>
    </button>
  ) : null;

  // ----------- TOOLBAR (shared) -----------
  const Toolbar = (
    <div className="chrome sticky top-16 z-30 border-b border-border/60 bg-background/85 backdrop-blur-md transition-opacity">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            data-testid="prev-chapter-btn"
            disabled={prevDisabled}
            onClick={() => go(-1)}
            aria-label={
              isTalmudBook
                ? (isEn ? "Previous page" : "דף קודם")
                : (isEn ? "Previous chapter" : "פרק קודם")
            }
            className="text-foreground hover:text-gold hover:bg-gold/10 disabled:opacity-30"
          >
            <ChevronRight className={`h-5 w-5 ${isEn ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </Button>
          <button
            data-testid="chapter-picker-btn"
            onClick={() => setPickerOpen(true)}
            className="font-heading text-sm sm:text-base md:text-lg text-foreground hover:text-gold px-2 sm:px-3 py-1.5 rounded-sm transition-colors max-w-[55vw] truncate"
          >
            {(isEn ? (book?.en || slug) : book?.he) || ""} · {chapterUnitWord}{chapterLabel}
          </button>
          <ChapterActionsMenu
            book={slug}
            bookHe={book?.he}
            chapter={chapter}
            verses={data?.verses || []}
            onBuildLesson={() => setOpenChapterBasket(true)}
          />
          <Button
            variant="ghost"
            size="icon"
            data-testid="next-chapter-btn"
            disabled={nextDisabled}
            onClick={() => go(1)}
            aria-label={
              isTalmudBook
                ? (isEn ? "Next page" : "דף הבא")
                : (isEn ? "Next chapter" : "פרק הבא")
            }
            className="text-foreground hover:text-gold hover:bg-gold/10 disabled:opacity-30"
          >
            <ChevronLeft className={`h-5 w-5 ${isEn ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <ModeToggle
            mode={readingMode}
            onModeChange={(newMode) => {
              update({ readingMode: newMode });
              // The book stays open across mode toggles — we used to
              // close it to replay the opening animation, but that
              // disorients users who switched modes mid-study (and the
              // intro already plays on the first visit per session
              // via `sessionStorage["tanakh_book_opened"]`).
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            data-testid="toggle-focus-btn"
            onClick={() => update({ focusMode: !focusMode })}
            aria-label={t("reader_extras.enter_focus_aria")}
            className="text-foreground hover:text-gold hover:bg-gold/10"
          >
            {focusMode ? (
              <Minimize2 className="h-4 w-4" strokeWidth={1.5} />
            ) : (
              <Maximize2 className="h-4 w-4" strokeWidth={1.5} />
            )}
          </Button>
          {/* Reader settings entry point hidden per product decision —
              the cog button + popover code lives on in ReaderSettings.jsx
              so we can re-enable later without re-implementation. */}
          {false && (
            <Button
              variant="ghost"
              size="icon"
              data-testid="open-settings-btn"
              onClick={() => setSettingsOpen(true)}
              aria-label={t("reader_extras.settings_aria")}
              className="text-foreground hover:text-gold hover:bg-gold/10"
            >
              <Settings2 className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>
      <BasketSheet
        open={openChapterBasket}
        onClose={() => setOpenChapterBasket(false)}
      />
    </div>
  );

  // ----------- BOOK MODE (3D page-flip) -----------
  if (readingMode === "book") {
    // The book canvas (center column) — same on mobile & desktop, but on
    // desktop it is embedded inside the DesktopReaderShell side-by-side
    // with the left navigator and the right verse-detail panel.
    const bookCanvas = (
      <div className={activeVerse && !isDesktop ? "is-dimmed-wrapper" : ""}>
        <div
          className={
            activeVerse && !isDesktop
              ? "pointer-events-none opacity-70 transition-all"
              : "transition-all"
          }
        >
          {loading ? (
            <div className="min-h-[60vh] flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="h-10 w-10 mx-auto rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
                <p className="font-heading text-muted-foreground">
                  {t("reader_extras.loading_book")}
                </p>
              </div>
            </div>
          ) : (
            data?.verses?.length > 0 && (
              <PinchZoomStage>
                {/* Language branch — English picks the parallel
                    BookReader3DEn implementation; Hebrew always uses
                    the original BookReader3D (byte-identical to pre-
                    English work). The two are intentionally separate
                    components so a tweak to one never touches the
                    other. */}
                {isEn ? (
                  <BookReader3DEn
                    ref={bookRef}
                    verses={data.verses}
                    bookHe={book?.he}
                    bookEn={book?.en}
                    chapter={chapter}
                    totalChapters={total}
                    onVerseClick={handleVerseClick}
                    activeVerseNum={activeVerse?.verse}
                    highlights={highlights}
                    notesByVerse={notesByVerse}
                    showVerseNums={showVerseNums}
                    onPrevChapter={() => !prevDisabled && go(-1)}
                    onNextChapter={() => !nextDisabled && go(1)}
                    hasPrev={!prevDisabled}
                    hasNext={!nextDisabled}
                    autoContinue={!!autoContinue}
                    nextChapterLabelEn={isTalmudBook ? "Continue to next page" : "Continue to next chapter"}
                    chapterLabelEnFn={isTalmudBook ? talmudDafLabelEn : undefined}
                    onPageFlip={(idx) => {
                      setLibPageIdx(idx);
                      const total = bookRef.current?.pageFlip?.()?.getPageCount?.();
                      if (typeof total === "number") setLibPageTotal(total);
                    }}
                  />
                ) : (
                  <BookReader3D
                    ref={bookRef}
                    verses={data.verses}
                    bookHe={book?.he}
                    bookEn={book?.en}
                    chapter={chapter}
                    totalChapters={total}
                    onVerseClick={handleVerseClick}
                    activeVerseNum={activeVerse?.verse}
                    highlights={highlights}
                    notesByVerse={notesByVerse}
                    nikud={nikud}
                    showVerseNums={showVerseNums}
                    onPrevChapter={() => !prevDisabled && go(-1)}
                    onNextChapter={() => !nextDisabled && go(1)}
                    hasPrev={!prevDisabled}
                    hasNext={!nextDisabled}
                    autoContinue={!!autoContinue}
                    nextChapterLabelHe={isTalmudBook ? t("reader_extras.next_page_label") : t("reader_extras.next_chapter_label")}
                    chapterLabelFn={isTalmudBook ? talmudDafLabel : undefined}
                    chapterLabelEnFn={isTalmudBook ? talmudDafLabelEn : undefined}
                    onPageFlip={(idx) => {
                      setLibPageIdx(idx);
                      // Pull total once the library has it; cheap to compute
                      // off the renderedPages length (= our pages length).
                      const total = bookRef.current?.pageFlip?.()?.getPageCount?.();
                      if (typeof total === "number") setLibPageTotal(total);
                    }}
                  />
                )}
              </PinchZoomStage>
            )
          )}
        </div>
        {/* Current-location floating pill */}
        {!loading && !focusMode && book && (
          <div
            data-testid="book-location-pill"
            className="pointer-events-none fixed top-20 left-1/2 -translate-x-1/2 z-20"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-card/80 px-4 py-1.5 text-xs font-heading text-foreground backdrop-blur">
              <span className="text-gold">{book?.he}</span>
              <span className="opacity-50">·</span>
              {chapterUnitWord}{chapterLabel}
            </span>
          </div>
        )}

      </div>
    );

    // ----- Mobile-only sticky bottom navigation bar (md:hidden) -----
    // Replaces the previous floating side-arrows that overlapped the verse text.
    // Includes:
    //   • RIGHT arrow → previous PAGE (flipBackward); jumps to previous chapter
    //     once the user is on the very first rendered page.
    //   • Center label  → current book + chapter (and chapter / total).
    //   • LEFT arrow  → next PAGE (flipForward); jumps to next chapter once
    //     the user is on the very last rendered page.
    // Hidden when overlay is open / focus mode active / data still loading.
    const handleFlipNext = () => {
      // Reading order is reversed in the library, so library index 0 ===
      // the LAST semantic page (next-cue when hasNext, otherwise last text).
      // One more "forward" tap from there must jump to the next chapter.
      if (libPageIdx === 0 && !nextDisabled) {
        go(1);
        return;
      }
      bookRef.current?.flipForward?.();
    };
    const handleFlipPrev = () => {
      // Library index === total-1 means the user is on the FIRST semantic
      // page. Going back from there should jump to the previous chapter.
      if (
        libPageTotal > 0 &&
        libPageIdx === libPageTotal - 1 &&
        !prevDisabled
      ) {
        go(-1);
        return;
      }
      bookRef.current?.flipBackward?.();
    };
    const flipPrevDisabled =
      libPageTotal > 0 &&
      libPageIdx === libPageTotal - 1 &&
      prevDisabled;
    const flipNextDisabled = libPageIdx === 0 && nextDisabled;

    const mobileFlipBar =
      !loading && !focusMode && !activeVerse && data?.verses?.length > 0 ? (
        <div
          data-testid="mobile-page-nav-bar"
          className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-gold/30 bg-background/95 backdrop-blur-md"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            {/* Leading-edge arrow → previous page.
                HE (RTL): right-pointing chevron sits on the right.
                EN (LTR): left-pointing chevron sits on the left.
                The button's *visual position* is decided by the parent
                flex container respecting dir; we just swap the icon. */}
            <button
              type="button"
              data-testid="mobile-flip-prev"
              onClick={handleFlipPrev}
              disabled={flipPrevDisabled}
              aria-label={t("reader_chrome.mobile_flip_prev_aria")}
              className="h-12 w-12 inline-flex items-center justify-center rounded-full bg-card/90 border border-gold/40 shadow-sm text-foreground hover:text-gold hover:bg-card transition-colors active:scale-95 disabled:opacity-30 disabled:active:scale-100"
            >
              {isEn ? (
                <ChevronLeft className="h-6 w-6" strokeWidth={1.5} />
              ) : (
                <ChevronRight className="h-6 w-6" strokeWidth={1.5} />
              )}
            </button>
            <div
              data-testid="mobile-page-nav-info"
              className="flex flex-col items-center text-center px-2 leading-tight"
            >
              <span className="font-heading text-sm text-foreground">
                {isEn ? (book?.en || slug) : book?.he} · {chapterUnitWord}{chapterLabel}
              </span>
              <span className="font-heading text-[11px] text-muted-foreground">
                {isTalmudBook || isEn
                  ? `${chapter} / ${total}`
                  : `${numberToHebrewLetter(chapter)} / ${numberToHebrewLetter(total)}`}
              </span>
            </div>
            {/* Trailing-edge arrow → next page. */}
            <button
              type="button"
              data-testid="mobile-flip-next"
              onClick={handleFlipNext}
              disabled={flipNextDisabled}
              aria-label={t("reader_chrome.mobile_flip_next_aria")}
              className="h-12 w-12 inline-flex items-center justify-center rounded-full bg-card/90 border border-gold/40 shadow-sm text-foreground hover:text-gold hover:bg-card transition-colors active:scale-95 disabled:opacity-30 disabled:active:scale-100"
            >
              {isEn ? (
                <ChevronRight className="h-6 w-6" strokeWidth={1.5} />
              ) : (
                <ChevronLeft className="h-6 w-6" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      ) : null;

    const bottomChapterNav =
      !loading && !focusMode ? (
        <div className="mx-auto max-w-3xl px-6 pb-10 hidden md:flex items-center justify-between">
          {!prevDisabled ? (
            <Button
              variant="ghost"
              onClick={() => go(-1)}
              className="sacred-btn-ghost"
              data-testid="book-prev-chapter"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              {chapterRefLabel(chapter - 1)}
            </Button>
          ) : (
            <div className="w-20"></div>
          )}
          <span className="font-heading text-sm text-muted-foreground">
            {totalLabel}
          </span>
          <Button
            variant="ghost"
            onClick={() => go(1)}
            disabled={nextDisabled}
            className="sacred-btn-ghost disabled:opacity-30"
            data-testid="book-next-chapter"
          >
            {chapterRefLabel(chapter + 1)}
            <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      ) : null;

    // Desktop: Beit Midrash — 3-panel resizable workspace.
    if (isDesktop && !focusMode) {
      const rightPanel = activeVerse ? (
        <VerseOverlay
          open={!!activeVerse}
          onClose={() => setActiveVerse(null)}
          book={slug}
          chapter={chapter}
          verse={activeVerse}
          bookHe={book?.he}
          bookEn={book?.en}
          existingNote={activeVerse ? notesByVerse[activeVerse.verse] : null}
          existingHighlight={
            activeVerse ? highlights[activeVerse.verse] : null
          }
          onUserDataChange={loadUserData}
          presentation="panel"
        />
      ) : null;

      return (
        <div
          className="relative flex flex-col"
          // 100dvh = dynamic viewport, so the mobile address-bar collapse
          // doesn't shrink the panel mid-scroll. The minus accounts only
          // for the global site header (h-18 = 4.5rem); the Toolbar and
          // BookNavRibbon below sit *inside* this column and contribute
          // their natural height, leaving the shell (flex-1 min-h-0) to
          // fill exactly what's left — no hardcoded subtraction needed.
          style={{ height: "calc(100dvh - 4.5rem)" }}
          data-testid="reader-page"
          data-book={slug}
          data-chapter={chapter}
          data-mode="book"
          data-layout="desktop"
        >
          {Toolbar}
          <BookNavRibbon currentBook={slug} />
          <DesktopReaderShell
            book={book}
            slug={slug}
            currentChapter={chapter}
            searchInputRef={searchInputRef}
            rightPanel={rightPanel}
            rightPanelOpen={!!activeVerse}
          >
            {bookCanvas}
            {bottomChapterNav}
          </DesktopReaderShell>
          <ReaderSettings
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
          <ChapterPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            book={book}
            currentChapter={chapter}
            onSelect={(ch) => {
              setPickerOpen(false);
              navigate(`/reader/${bookUrl(slug)}/${ch}`);
            }}
          />
        </div>
      );
    }

    // Mobile/tablet: current single-column layout.
    return (
      <div
        className={`${focusMode ? "focus-mode" : ""} relative`}
        data-testid="reader-page"
        data-book={slug}
        data-chapter={chapter}
        data-mode="book"
        data-layout="mobile"
      >
        {ExitFocusButton}
        {Toolbar}
        <BookNavRibbon currentBook={slug} />
        {bookCanvas}
        {bottomChapterNav}
        {mobileFlipBar}

        <VerseOverlay
          open={!!activeVerse}
          onClose={() => setActiveVerse(null)}
          book={slug}
          chapter={chapter}
          verse={activeVerse}
          bookHe={book?.he}
          bookEn={book?.en}
          existingNote={activeVerse ? notesByVerse[activeVerse.verse] : null}
          existingHighlight={
            activeVerse ? highlights[activeVerse.verse] : null
          }
          onUserDataChange={loadUserData}
        />
        <ReaderSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        <ChapterPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          book={book}
          currentChapter={chapter}
          onSelect={(ch) => {
            setPickerOpen(false);
            navigate(`/reader/${bookUrl(slug)}/${ch}`);
          }}
        />
      </div>
    );
  }

  // ----------- STUDY MODE -----------
  // Desktop: 3-pane Mikraot Gedolot workspace (text · commentaries · basket+notes).
  // Mobile/tablet: keep the original single-column flat scroll for clarity.
  if (readingMode === "study" && isDesktop && !focusMode) {
    return (
      <div
        className="relative"
        data-testid="reader-page"
        data-book={slug}
        data-chapter={chapter}
        data-mode="study"
        data-layout="desktop"
      >
        {Toolbar}
        <BookNavRibbon currentBook={slug} />
        <MikraotShell
          book={book}
          bookHe={book?.he}
          slug={slug}
          chapter={chapter}
          total={total}
          verses={data?.verses || []}
          versionEn={data?.version_en}
          loading={loading}
          activeVerse={activeVerse}
          setActiveVerse={setActiveVerse}
          highlights={highlights}
          notesByVerse={notesByVerse}
          showVerseNums={showVerseNums}
          onPrevChapter={() => !prevDisabled && go(-1)}
          onNextChapter={() => !nextDisabled && go(1)}
          prevDisabled={prevDisabled}
          nextDisabled={nextDisabled}
        />
        <ReaderSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        <ChapterPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          book={book}
          currentChapter={chapter}
          onSelect={(ch) => {
            setPickerOpen(false);
            navigate(`/reader/${bookUrl(slug)}/${ch}`);
          }}
        />
      </div>
    );
  }

  // ----------- STUDY MODE (mobile / focus — flat scroll) -----------
  return (
    <div
      className={`${focusMode ? "focus-mode" : ""} relative`}
      data-testid="reader-page"
      data-book={slug}
      data-chapter={chapter}
      data-mode="study"
    >
      {ExitFocusButton}
      {Toolbar}

      <article
        ref={contentRef}
        data-testid="chapter-content"
        className={`mx-auto max-w-3xl px-6 sm:px-10 md:px-16 py-10 md:py-16 ${sizeClass} ${noNikudClass}`}
      >
        <header className="text-center mb-10">
          <p className="font-latin italic text-xs tracking-widest uppercase text-gold mb-2">
            {book?.en} · {isTalmudBook ? `Daf ${data?.amud_label || chapter}` : `Chapter ${chapter}`}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-foreground">{book?.he}</h1>
          <p className="font-heading text-xl md:text-2xl text-gold mt-2">{chapterUnitWord}{chapterLabel}</p>
          <div className="ornamental-divider mx-auto max-w-xs mt-6 text-lg">✦</div>
        </header>

        {loading && (
          <div className="space-y-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="h-8 shimmer-bg rounded"
                style={{ width: `${60 + Math.random() * 40}%` }}
              />
            ))}
          </div>
        )}

        {!loading && data?.verses?.length > 0 && (
          <div className="torah-verse hebrew-text leading-loose" dir="rtl">
            {data.verses.map((v) => {
              const hl = highlights[v.verse];
              const hasNote = !!notesByVerse[v.verse];
              const hlClass = hl ? `hl-${hl.color}` : "";
              return (
                <span
                  key={v.verse}
                  data-testid={`verse-${v.verse}`}
                  onClick={() => handleVerseClick(v)}
                  className={`verse-row ${activeVerse?.verse === v.verse ? "active" : ""} ${hlClass}`}
                >
                  {showVerseNums && (
                    <span className="verse-num">{numberToHebrewLetter(v.verse)}</span>
                  )}
                  <span>{applyNikud(v.he)}</span>
                  {hasNote && (
                    <Bookmark
                      className="inline-block h-3 w-3 text-gold align-middle mx-1"
                      strokeWidth={1.5}
                    />
                  )}{" "}
                </span>
              );
            })}
          </div>
        )}

        {!loading && (!data?.verses || data.verses.length === 0) && (
          <div className="text-center text-muted-foreground py-12">
            <BookOpen className="h-10 w-10 mx-auto mb-4 text-gold" strokeWidth={1.5} />
            <p>{t("reader_extras.no_text_loaded")}</p>
          </div>
        )}

        <div className="ornamental-divider mt-14 text-lg">✦ ❖ ✦</div>

        <div className="mt-10 flex items-center justify-between">
          {!prevDisabled ? (
            <Button
              variant="ghost"
              onClick={() => go(-1)}
              className="sacred-btn-ghost"
              data-testid="bottom-prev-btn"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              {chapterRefLabel(chapter - 1)}
            </Button>
          ) : (
            <div className="w-20"></div>
          )}
          <span className="font-heading text-sm text-muted-foreground">
            {totalLabel}
          </span>
          <Button
            variant="ghost"
            onClick={() => go(1)}
            disabled={nextDisabled}
            className="sacred-btn-ghost disabled:opacity-30"
            data-testid="bottom-next-btn"
          >
            {chapterRefLabel(chapter + 1)}
            <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground/80">
          טקסט מתוך Sefaria · רישיון {data?.license || "CC BY"} · {data?.attribution || "Sefaria"}
        </p>
      </article>

      <VerseOverlay
        open={!!activeVerse}
        onClose={() => setActiveVerse(null)}
        book={slug}
        chapter={chapter}
        verse={activeVerse}
        bookHe={book?.he}
        bookEn={book?.en}
        existingNote={activeVerse ? notesByVerse[activeVerse.verse] : null}
        existingHighlight={activeVerse ? highlights[activeVerse.verse] : null}
        onUserDataChange={loadUserData}
      />
      <ReaderSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <ChapterPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        book={book}
        currentChapter={chapter}
        onSelect={(ch) => {
          setPickerOpen(false);
          navigate(`/reader/${bookUrl(slug)}/${ch}`);
        }}
      />
    </div>
  );
}
